import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, UserPlus, Check } from "lucide-react";
import { searchProfiles } from "@/lib/api";
import { initials } from "@/lib/scoring";

// Search the shared player directory and add profiles to the trip.
export default function PlayerDirectoryPicker({ existingProfileIds, onPick, disabled }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      setLoading(true);
      try { setResults(await searchProfiles(q.trim())); } catch { toast.error("Directory unavailable"); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  return (
    <div className="border border-dashed border-[rgba(27,51,34,0.25)] rounded-lg p-2" data-testid="player-directory">
      <div className="flex items-center gap-2">
        <Search size={14} className="text-[color:var(--brass-dark)] shrink-0" />
        <input
          data-testid="player-directory-search"
          className="tga-input-light flex-1 !py-1"
          placeholder="Find a player in the directory…"
          value={q}
          disabled={disabled}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        />
        {open && <button data-testid="player-directory-close" className="ghost-btn rounded-md px-2 py-1 text-xs" onClick={() => setOpen(false)}>Done</button>}
      </div>
      {open && (
        <div className="mt-2 max-h-56 overflow-y-auto space-y-1" data-testid="player-directory-results">
          {loading && results.length === 0 && <div className="helptext px-1">Searching…</div>}
          {!loading && results.length === 0 && <div className="helptext px-1">No players found{q ? ` for "${q}"` : ""}. Add them below and tick "save to directory".</div>}
          {results.map((p) => {
            const added = existingProfileIds.has(p.id);
            const label = p.nickname || p.name;
            return (
              <button
                key={p.id}
                data-testid={`player-directory-item-${p.id}`}
                disabled={added || disabled}
                onClick={() => { onPick(p); setQ(""); }}
                className="w-full flex items-center gap-2 tga-row rounded-lg px-2 py-1.5 text-left disabled:opacity-50"
              >
                <div className="avatar shrink-0" style={{ width: 28, height: 28, fontSize: 12 }}>{initials(label)}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{label}{p.nickname && <span className="opacity-60 text-xs ml-1">({p.name})</span>}</div>
                  <div className="text-[11px] text-[color:var(--brass-dark)]">HCP {p.handicap}{p.linked ? " · verified account" : ""}</div>
                </div>
                {added ? <Check size={14} className="text-emerald-700" /> : <UserPlus size={14} className="text-[color:var(--brass-dark)]" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
